import { Signal, computed, signal } from '@angular/core';
import { AemsFile } from '../../models/aems-file.model';
import { MaintenanceOrderPart } from '../../models/maintenance-order-part';

export type RepairLineItemFilterKey = 'damageCode' | 'repairCode' | 'description' | 'repairLocationCode' | 'tpiCode';
export type FilterableRepairLineItem = Record<RepairLineItemFilterKey, string> & {
  id: string;
  parts?: MaintenanceOrderPart[];
  files: AemsFile[];
};

export const repairLineItemFilterFields: ReadonlyArray<{ id: RepairLineItemFilterKey; label: string }> = [
  { id: 'damageCode', label: 'Damage Code' },
  { id: 'repairCode', label: 'Repair Code' },
  { id: 'description', label: 'Description' },
  { id: 'repairLocationCode', label: 'Repair Loc Code' },
  { id: 'tpiCode', label: 'TPI Code' },
];

const emptyFilters: Record<RepairLineItemFilterKey, string> = {
  damageCode: '',
  repairCode: '',
  description: '',
  repairLocationCode: '',
  tpiCode: '',
};

// Per-column text filters and expand/collapse-all state shared by the repair line item tables
export class RepairLineItemsFilter<T extends FilterableRepairLineItem> {
  readonly filters = signal<Record<RepairLineItemFilterKey, string>>({ ...emptyFilters });
  readonly hasActiveFilters = computed(() => Object.values(this.filters()).some((value) => value.trim() !== ''));
  readonly allExpanded = signal(true);
  readonly filteredData = computed(() => {
    const activeFilters = Object.entries(this.filters())
      .map(([key, value]) => [key as RepairLineItemFilterKey, value.trim().toLowerCase()] as const)
      .filter(([, value]) => value !== '');
    if (activeFilters.length === 0) return this.rows();
    return this.rows().filter((item) => activeFilters.every(([key, value]) => (item[key] || '').toLowerCase().includes(value)));
  });
  readonly rowsWithParts = computed(() => this.filteredData().filter((item) => (item.parts?.length ?? 0) > 0));
  readonly expandedRows = computed(() => (this.allExpanded() ? this.rowsWithParts() : []));

  constructor(readonly rows: Signal<T[]>) {}

  setFilter(key: RepairLineItemFilterKey, value: string) {
    this.filters.update((filters) => ({ ...filters, [key]: value }));
  }

  clearFilters() {
    this.filters.set({ ...emptyFilters });
  }

  toggleExpandAll() {
    this.allExpanded.update((expanded) => !expanded);
  }
}
